import React, { Component, createRef, KeyboardEvent, RefObject } from 'react';
import { compose } from 'recompose';
import { noop, pick } from 'lodash';
import { ContentState, DraftHandleValue, EditorState, getDefaultKeyBinding } from 'draft-js';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import DraftPluginEditor from 'draft-js-plugins-editor';

import { PublicUserProfile } from 'api';
import { IWithUser, withUser } from 'providers/UserContext';

import {
  EDITOR_SUBMIT,
  HANDLED,
  MENTIONS_STYLE_KEYS,
  NOT_HANDLED,
  TOOLBAR_BUTTON_STYLE_KEYS,
  TOOLBAR_STYLE_KEYS,
} from './constants';

import convertToText from './convertToText';
import createEditorInstance from './createEditorInstance';
import { createMentionPlugin, createToolbarPlugin, linkifyPlugin, linkPlugin } from './plugins';
import {
  Format,
  IEditorProps,
  IMentionPlugin,
  IMentionsTheme,
  IToolbarButtonTheme,
  IToolbarPlugin,
  IToolbarTheme,
  Modes,
} from './types';
import styles from './styles';
import EditorToolbar from './Components/Toolbar';
import Mentions from './Components/Mentions';
import convertToHTML from './convertToHTML';

export interface IEnhancedProps extends IEditorProps, IWithUser, WithStyles<typeof styles> {}

interface IState {
  error: string;
  editorState: EditorState;
  filteredUsers: PublicUserProfile[];
  mentionsOpen: boolean;
}

const enhancers = compose<IEnhancedProps, IEditorProps>(withUser, withStyles(styles));

const defaultProps = {
  text: '',
  placeholder: '',
  className: '',
  disableMentions: false,
  disableToolbar: false,
  focusOnMount: false,
  submitOnEnter: false,
  onAddMention: noop,
  onEnter: noop,
  onEscape: noop,
  onChange: noop,
  onSpace: noop,
  onSubmit: noop,
  onPaste: noop,
};

class Editor extends Component<IEnhancedProps, IState> {
  static defaultProps = defaultProps;

  state: IState;
  mentionPlugin: IMentionPlugin;
  toolbarPlugin: IToolbarPlugin;
  editor: RefObject<HTMLDivElement>;
  unmounted: boolean;

  constructor(props: IEnhancedProps) {
    super(props);

    const editorState = props.text ? createEditorInstance(props) : EditorState.createEmpty();

    this.editor = createRef();
    this.unmounted = false;

    if (!props.disableMentions) {
      this.mentionPlugin = createMentionPlugin({
        theme: pick(props.classes, MENTIONS_STYLE_KEYS) as IMentionsTheme,
      });
    }

    if (!props.disableToolbar) {
      this.toolbarPlugin = createToolbarPlugin({
        theme: {
          toolbarStyles: pick(props.classes, TOOLBAR_STYLE_KEYS) as IToolbarTheme,
          buttonStyles: pick(props.classes, TOOLBAR_BUTTON_STYLE_KEYS) as IToolbarButtonTheme,
        },
      });
    }

    this.state = {
      error: '',
      filteredUsers: [],
      mentionsOpen: false,
      editorState,
    };
  }

  componentDidMount() {
    const { focusOnMount } = this.props;

    if (focusOnMount) {
      window.requestAnimationFrame(this.focus);
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  getValue(): string {
    const { format } = this.props;
    const { editorState } = this.state;

    return format === Format.text ? convertToText(editorState) : convertToHTML(editorState);
  }

  focus = () => {
    this.editor.current.focus();
  };

  keyBindingFn = (event: KeyboardEvent<{}>): string => {
    const { onEnter, onSpace, submitOnEnter } = this.props;
    const { filteredUsers } = this.state;

    const { shiftKey, key } = event;
    const isSpacebar = key === ' ';
    const isEnter = key === 'Enter';
    const isEscape = key === 'Escape';

    if (isEnter) {
      onEnter(this.getValue());
    }

    if (isSpacebar) {
      onSpace(this.getValue());

      if (filteredUsers.length) {
        this.setState({ filteredUsers: [] });
      }
    }

    if (isEscape) {
      this.onEscape();
    }

    if (!submitOnEnter) {
      return getDefaultKeyBinding(event);
    }

    const preventSubmit = key !== 'Enter' || shiftKey;
    const missingValue = !this.getValue().trim().length;

    if (preventSubmit || missingValue) {
      return getDefaultKeyBinding(event);
    }

    return EDITOR_SUBMIT;
  };

  handleReset() {
    const { editorState } = this.state;

    const nextState = EditorState.push(
      editorState,
      ContentState.createFromText(''),
      'remove-range',
    );

    this.setState({ editorState: EditorState.moveFocusToEnd(nextState) });
  }

  handleKeyCommand = (command: string): DraftHandleValue => {
    const { mode, onSubmit } = this.props;

    if (command === EDITOR_SUBMIT) {
      onSubmit(this.getValue());

      if (mode === Modes.create) {
        this.handleReset();
      }

      return HANDLED;
    }

    return NOT_HANDLED;
  };

  handleMentionUsers = (filteredUsers: PublicUserProfile[]) => {
    this.setState({ filteredUsers });
  };

  handleMentionToggle = (mentionsOpen: boolean) => {
    this.setState({ mentionsOpen });
  };

  onEscape = () => {
    const { onEscape } = this.props;
    const { mentionsOpen } = this.state;

    if (!mentionsOpen) {
      onEscape();
    }
  };

  onChange = (editorState: EditorState) => {
    const { onChange } = this.props;

    this.setState({ editorState }, () => {
      onChange(this.getValue());
    });
  };

  render() {
    const {
      className,
      disableMentions,
      disableToolbar,
      placeholder,
      onPaste,
      onAddMention,
    } = this.props;
    const { editorState, filteredUsers } = this.state;

    const plugins = [linkifyPlugin, linkPlugin];

    if (!disableToolbar) {
      plugins.push(this.toolbarPlugin);
    }

    if (!disableMentions) {
      plugins.push(this.mentionPlugin);
    }

    return (
      <div className={className}>
        <DraftPluginEditor
          spellCheck
          stripPastedStyles
          ref={this.editor}
          editorState={editorState}
          onChange={this.onChange}
          handlePastedText={(text: string) => {
            onPaste(text);
            return false;
          }}
          plugins={plugins}
          keyBindingFn={this.keyBindingFn}
          handleKeyCommand={this.handleKeyCommand}
          placeholder={placeholder}
        />
        {!disableToolbar && (
          <EditorToolbar plugins={{ toolbar: this.toolbarPlugin, link: linkPlugin }} />
        )}
        {!disableMentions && (
          <Mentions
            plugin={this.mentionPlugin}
            onChange={this.handleMentionUsers}
            onAddMention={onAddMention}
            users={filteredUsers}
            toggleMentionState={this.handleMentionToggle}
          />
        )}
      </div>
    );
  }
}

export default enhancers(Editor);
